import { platformApi } from '@/apis';
import { featureSortFn, filterType, reviveDate } from '@/utils';
import { captureMessage } from '@sentry/react';
import { parseServerSentEvents } from 'parse-sse';
import { useEffect, useRef, useState } from 'react';

export function useStreamingVehiclesFeatureCollection(
  search,
  sort,
  filter,
  enabled = true,
) {
  const identificationNumberLookup = useRef({});
  const imeiLookup = useRef({});
  const ctrl = useRef(null);
  const [featureCollection, setFeatureCollection] = useState({
    type: 'FeatureCollection',
    features: [],
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    ctrl.current = new AbortController();

    async function initiateStreams() {
      try {
        const trackerValuesSource = platformApi.get('sse/tracker-values', {
          signal: ctrl.current.signal,
          headers: { accept: 'text/event-stream' },
        });
        const vehiclesSource = platformApi.get('sse/vehicles', {
          signal: ctrl.current.signal,
          headers: { accept: 'text/event-stream' },
        });
        const [trackerValues, vehicles] = await Promise.all([
          trackerValuesSource,
          vehiclesSource,
        ]);

        await Promise.all([
          (async () => {
            for await (const msg of parseServerSentEvents(trackerValues)) {
              if (msg.data) {
                const newFeature = JSON.parse(msg.data, reviveDate);

                const vehicle =
                  identificationNumberLookup.current[
                    newFeature.properties.vehicleIdentificationNumber
                  ] || imeiLookup.current[newFeature.properties.imei];

                if (vehicle) {
                  newFeature.properties.vehicle = vehicle;
                }

                setFeatureCollection((featureCollection) => {
                  const alreadyExists = featureCollection.features.some(
                    (feature) => feature.id === newFeature.id,
                  );

                  if (alreadyExists && msg.event === 'initial') {
                    return featureCollection;
                  }

                  return {
                    ...featureCollection,
                    features: alreadyExists
                      ? featureCollection.features.map((feature) =>
                          feature.id === newFeature.id ? newFeature : feature,
                        )
                      : [...featureCollection.features, newFeature],
                  };
                });
              }
            }
          })(),
          (async () => {
            for await (const msg of parseServerSentEvents(vehicles)) {
              if (msg.data) {
                const vehicle = JSON.parse(msg.data);

                identificationNumberLookup.current[
                  vehicle.identificationNumber
                ] = vehicle;

                if (vehicle.telematicsBoxImei) {
                  imeiLookup.current[vehicle.telematicsBoxImei] = vehicle;
                }

                setFeatureCollection((featureCollection) => ({
                  ...featureCollection,
                  features: featureCollection.features.map((feature) =>
                    feature.properties.vehicleIdentificationNumber ===
                      vehicle.identificationNumber ||
                    feature.properties.imei === vehicle.telematicsBoxImei
                      ? {
                          ...feature,
                          properties: {
                            ...feature.properties,
                            vehicle,
                          },
                        }
                      : feature,
                  ),
                }));
              }
            }
          })(),
        ]);
      } catch (error) {
        if (error.name === 'AbortError') {
          captureMessage('Stream aborted', { level: 'debug' });
        } else {
          throw error;
        }
      }
    }

    initiateStreams();

    return () => ctrl.current.abort();
  }, [enabled]);

  return {
    ...featureCollection,
    features: featureCollection.features
      .filter(({ properties }) => filterType('vehicles', filter, properties))
      .filter(({ properties }) =>
        JSON.stringify(properties).toLowerCase().includes(search.toLowerCase()),
      )
      .sort(featureSortFn(sort)),
    total: featureCollection.features.length,
  };
}
